import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomersReport, CustomersReportQuery } from '@optiflow/types';
import { Card } from '@/components/ui/Card';
import { CustomersRecords, type CustomersRecordsRequest } from './CustomersRecords';
import { MoneyByCurrencyText } from './MoneyByCurrencyText';
import { Section, SimpleTable, Stat, CountText, ViewRecords } from './ReportBlocks';

const InventoryCountChart = lazy(() => import('./InventoryCountChart'));

/**
 * The Customers tab.
 *
 * Three things kept apart on purpose: who registered where (the registering branch, and nothing
 * else), what customers bought (canonical Sales by the order's branch) and what they still owe
 * (the canonical receivable, as of now). Walk-in sales and balances sit beside the customers so the
 * totals still match the Sales and Finance tabs.
 */
export function CustomersContent({
  report,
  query,
}: {
  report: CustomersReport;
  query: CustomersReportQuery;
}) {
  const { t } = useTranslation();
  const count = (value: number) => <CountText value={value} />;
  const [open, setOpen] = useState<CustomersRecordsRequest | null>(null);
  const view = (request: CustomersRecordsRequest) => (
    <ViewRecords title={request.title} onOpen={() => setOpen(request)} />
  );
  const col = (key: string) => t(`reports.customers.columns.${key}`);
  const name = (row: { name: string | null; customerNumber: string | null }) => (
    <span>
      {row.name ?? t('reports.customers.unknownCustomer')}{' '}
      {row.customerNumber ? (
        <span className="text-muted text-xs" dir="ltr">
          {row.customerNumber}
        </span>
      ) : null}
    </span>
  );
  const { registrations, sales, receivables } = report;

  return (
    <div className="space-y-6">
      <Section
        id="customers-registrations"
        title={t('reports.customers.registrationsTitle')}
        note={t('reports.customers.registrationsNote')}
      >
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <Stat
            label={t('reports.customers.kpis.registered')}
            action={view({
              kind: 'REGISTRATIONS',
              title: t('reports.customers.kpis.registered'),
            })}
          >
            {count(registrations.registered)}
          </Stat>
        </div>
        <Card className="min-w-0 p-4">
          <h3 className="text-ink font-semibold">{t('reports.customers.byRegisteringBranch')}</h3>
          <Suspense fallback={null}>
            <InventoryCountChart
              label={t('reports.customers.kpis.registered')}
              data={registrations.trend.buckets.map((bucket, index) => ({
                label: bucket.from === bucket.to ? bucket.from : `${bucket.from} – ${bucket.to}`,
                value: registrations.trend.values[index] ?? 0,
              }))}
            />
          </Suspense>
          <SimpleTable
            headers={[col('registeredAt'), col('registered')]}
            ends={[1]}
            rows={[
              ...registrations.byBranch.map((row) => ({
                key: row.branchId,
                cells: [row.branchName, count(row.registered)],
              })),
              // Customers registered with no branch are shown apart and never folded into a branch; null means one registration branch is selected and they fall outside it.
              ...(registrations.noRegistrationBranch === null
                ? []
                : [
                    {
                      key: 'no-registration-branch',
                      cells: [
                        t('reports.customers.noRegistrationBranch'),
                        count(registrations.noRegistrationBranch),
                      ],
                    },
                  ]),
            ]}
          />
        </Card>
      </Section>

      {open && open.kind === 'REGISTRATIONS' ? (
        <CustomersRecords
          key="registrations"
          query={query}
          request={open}
          onClose={() => setOpen(null)}
        />
      ) : null}

      {sales ? (
        <Section
          id="customers-sales"
          title={t('reports.customers.salesTitle')}
          note={t('reports.customers.salesNote')}
        >
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Stat
              label={t('reports.customers.kpis.buying')}
              action={view({
                kind: 'CUSTOMER_ACTIVITY',
                title: t('reports.customers.kpis.customerNetSales'),
              })}
            >
              {count(sales.buyingCustomers)}
            </Stat>
            <Stat label={t('reports.customers.kpis.returning')}>
              {count(sales.returningCustomers)}
            </Stat>
            <Stat label={t('reports.customers.kpis.firstTime')}>
              {count(sales.firstTimeBuyers)}
            </Stat>
            <Stat
              label={t('reports.customers.kpis.customerNetSales')}
              hint={t('reports.customers.hints.orders', { count: sales.customerConfirmedOrders })}
            >
              <MoneyByCurrencyText amounts={sales.customerNetSales} />
            </Stat>
            <Stat
              label={t('reports.customers.kpis.walkInNetSales')}
              hint={t('reports.customers.hints.orders', { count: sales.walkIn.confirmedOrders })}
            >
              <MoneyByCurrencyText amounts={sales.walkIn.netSales} />
            </Stat>
            <Stat
              label={t('reports.customers.kpis.netSales')}
              hint={t('reports.customers.hints.sameAsSales')}
            >
              <MoneyByCurrencyText amounts={sales.netSales} />
            </Stat>
          </div>
          <Card className="min-w-0 p-4">
            <h3 className="text-ink font-semibold">
              {t(`reports.customers.ranking.title.${report.ranking}`)}
            </h3>
            {sales.topCustomers.length === 0 ? (
              <p className="text-muted mt-3 text-sm">{t('reports.customers.empty')}</p>
            ) : (
              <SimpleTable
                headers={[
                  col('customer'),
                  col('orders'),
                  col('confirmed'),
                  col('returns'),
                  col('cancellations'),
                  col('netSales'),
                  '',
                ]}
                ends={[1, 2, 3, 4, 5]}
                rows={sales.topCustomers.map((row) => {
                  const label = row.name ?? t('reports.customers.unknownCustomer');
                  return {
                    key: row.customerId,
                    cells: [
                      name(row),
                      count(row.confirmedOrders),
                      <MoneyByCurrencyText key="c" amounts={row.confirmedSales} />,
                      <MoneyByCurrencyText key="r" amounts={row.returns} />,
                      <MoneyByCurrencyText key="x" amounts={row.cancellations} />,
                      <MoneyByCurrencyText key="n" amounts={row.netSales} />,
                      view({
                        kind: 'CUSTOMER_SALES',
                        customerId: row.customerId,
                        title: t('reports.customers.records.salesOf', { name: label }),
                      }),
                    ],
                  };
                })}
              />
            )}
          </Card>
          <Card className="min-w-0 p-4">
            <h3 className="text-ink font-semibold">{t('reports.customers.bySalesBranch')}</h3>
            <SimpleTable
              headers={[col('salesBranch'), col('buying'), col('orders'), col('netSales')]}
              ends={[1, 2, 3]}
              rows={sales.byBranch.map((row) => ({
                key: row.branchId,
                cells: [
                  row.branchName,
                  count(row.buyingCustomers),
                  count(row.customerConfirmedOrders),
                  <MoneyByCurrencyText key="n" amounts={row.customerNetSales} />,
                ],
              }))}
            />
            <p className="text-muted mt-2 text-xs">{t('reports.customers.distinctNote')}</p>
          </Card>
        </Section>
      ) : null}

      {open && (open.kind === 'CUSTOMER_ACTIVITY' || open.kind === 'CUSTOMER_SALES') ? (
        <CustomersRecords
          key={`${open.kind}-${open.customerId ?? ''}`}
          query={query}
          request={open}
          onClose={() => setOpen(null)}
        />
      ) : null}

      {receivables ? (
        <Section
          id="customers-receivables"
          title={t('reports.customers.receivablesTitle')}
          note={t('reports.customers.receivablesNote')}
        >
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <Stat
              label={t('reports.customers.kpis.customerOutstanding')}
              hint={t('reports.customers.hints.customers', { count: receivables.owingCustomers })}
              action={view({
                kind: 'OWING_CUSTOMERS',
                title: t('reports.customers.kpis.customerOutstanding'),
              })}
            >
              <MoneyByCurrencyText amounts={receivables.outstanding} />
            </Stat>
            <Stat
              label={t('reports.customers.kpis.walkInOutstanding')}
              hint={t('reports.customers.hints.orders', { count: receivables.walkIn.owingOrders })}
            >
              <MoneyByCurrencyText amounts={receivables.walkIn.outstanding} />
            </Stat>
            <Stat
              label={t('reports.customers.kpis.totalOutstanding')}
              hint={t('reports.customers.hints.sameAsFinance')}
            >
              <MoneyByCurrencyText amounts={receivables.total} />
            </Stat>
          </div>
          <Card className="min-w-0 p-4">
            <h3 className="text-ink font-semibold">{t('reports.customers.topOwing')}</h3>
            {receivables.topCustomers.length === 0 ? (
              <p className="text-muted mt-3 text-sm">{t('reports.customers.nothingOwed')}</p>
            ) : (
              <SimpleTable
                headers={[col('customer'), col('owingOrders'), col('outstanding'), '']}
                ends={[1, 2]}
                rows={receivables.topCustomers.map((row) => {
                  const label = row.name ?? t('reports.customers.unknownCustomer');
                  return {
                    key: row.customerId,
                    cells: [
                      name(row),
                      count(row.owingOrders),
                      <MoneyByCurrencyText key="o" amounts={row.outstanding} />,
                      view({
                        kind: 'CUSTOMER_OWING_ORDERS',
                        customerId: row.customerId,
                        title: t('reports.customers.records.owingOf', { name: label }),
                      }),
                    ],
                  };
                })}
              />
            )}
          </Card>
          <Card className="min-w-0 p-4">
            <h3 className="text-ink font-semibold">{t('reports.customers.byOrderBranch')}</h3>
            <SimpleTable
              headers={[col('salesBranch'), col('owingCustomers'), col('outstanding')]}
              ends={[1, 2]}
              rows={receivables.byBranch.map((row) => ({
                key: row.branchId,
                cells: [
                  row.branchName,
                  count(row.owingCustomers),
                  <MoneyByCurrencyText key="o" amounts={row.outstanding} />,
                ],
              }))}
            />
          </Card>
        </Section>
      ) : null}

      {open && (open.kind === 'OWING_CUSTOMERS' || open.kind === 'CUSTOMER_OWING_ORDERS') ? (
        <CustomersRecords
          key={`${open.kind}-${open.customerId ?? ''}`}
          query={query}
          request={open}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
}
